import React from 'react';
import { createSelector } from 'reselect';
import { RootState } from '../../../rootReducer';
import AnnotationTableAction from './AnnotationsTableAction';
import { shouldShowFinding } from '../Canvas/Helpers';
import { ColumnFilterItem, ColumnFilterNumberItem, ColumnProps, PropertyColumnData, StatusColumnItem } from '../../Shared/TableOptions/types';
import { populateIsVisibleColumnsFlag } from '../../Shared/TableOptions/Helpers';
import { savedKeys } from './Helpers';
import { getBladeAndBladeSideFromSequencePath } from '../../../shared/parsers/windTurbineParser';
import styles from './Annotations.module.scss';
import SliderFilter from './SliderFilter';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import StatusFilter from './StatusFilter';
import { isStringNullOrEmpty } from '../Annotation/AnnotationForm/Utils';

const getGrid = (state: RootState) => state.annotation.annotationsGrid;
const getStandard = (state: RootState) => state.annotation.annotationStandardDetails;
const getShowAllFindings = (state: RootState) => state.canvas.toggleAllUserFindingsVisibility;
const getShowMLFindings = (state: RootState) => state.canvas.toggleMLFindingsVisibility;
const getAllHiddenColumns = (state: RootState) => state.annotation.allHiddenColumns;
const { SAVED_HIDDEN_COLUMNS_KEY, SAVED_COLUMNS_ORDER_KEY } = savedKeys;

export const prepareGridDataSelector = createSelector(
  [getGrid, getStandard, getShowAllFindings, getShowMLFindings, getAllHiddenColumns],
  (gridData, standard, showAll, showML, allHiddenColumns) => {
    if (!gridData || !standard) {
      return {
        columns: [],
        dataSource: []
      };
    }

    const numberValueUnites = ['m', 'mm', 'mm2'];

    const allProperties: PropertyColumnData[] = standard.findingTypes
      .map(x =>
        x.findingTypeTemplate ? x.findingTypeTemplate.properties.map(y => ({ id: y.id, name: y.name, fullName: y.fullName, unit: y.units })) : []
      )
      .flat();

    const propertiesWithoutShapes = [
      ...new Set(allProperties.map(x => ({ name: x.name, unit: x.unit })).filter(x => !x.name.toLowerCase().includes('shape')))
    ];

    const uniquepropertiesWithNumberUnites = [...new Set(propertiesWithoutShapes.filter(x => numberValueUnites.includes(x.unit)).map(x => x.name))];

    const uniquePropertiesWithoutNumberUnites = [
      ...new Set(propertiesWithoutShapes.filter(x => !numberValueUnites.includes(x.unit)).map(x => x.name))
    ];

    const [bladeFilters, bladeSideFilters] = getBladeAndBladeSideColumnFilters(gridData);

    let columns: ColumnProps[] = [
      {
        ...getCommonColumnProperties('actions', false, false),
        title: 'Actions',
        render: (record: any): JSX.Element => <AnnotationTableAction record={record} />,
        sorter: false,
        onFilter: undefined
      },
      {
        ...getCommonColumnProperties('blade', true),
        title: 'Blade',
        filters: bladeFilters
      },
      {
        ...getCommonColumnProperties('bladeSide', true),
        title: 'Blade Side',
        filters: bladeSideFilters
      },
      ...uniquePropertiesWithoutNumberUnites.map(propertyName => ({
        ...getCommonColumnProperties(propertyName, true),
        title: getPropertyFullName(propertyName, allProperties),
        render: renderField(propertyName, getPropertyUnit(propertyName, allProperties)),
        filters: getColumnFilters(gridData, propertyName)
      })),
      ...uniquepropertiesWithNumberUnites.map(propertyName => ({
        ...getCommonColumnPropertiesWithUnites(propertyName, getColumnFiltersSlider(gridData, propertyName), true),
        title: getPropertyFullName(propertyName, allProperties),
        render: renderField(propertyName, getPropertyUnit(propertyName, allProperties))
      })),
      {
        ...getStatusColumnProperties('statusText', getStatusColumnFilters(gridData), true),
        title: 'Status',
        filters: getColumnFilters(gridData, 'statusText')
      },
      {
        ...getCommonColumnProperties('findingType', true),
        title: 'Finding Type',
        filters: getColumnFilters(gridData, 'findingTypeName')
      },
      {
        ...getCommonColumnProperties('findingTypeMlSuggestion', true),
        title: 'ML suggested Finding Type',
        filters: getColumnFilters(gridData, 'findingTypeMlSuggestion')
      }
    ];

    populateIsVisibleColumnsFlag(columns, SAVED_HIDDEN_COLUMNS_KEY);

    let dataSource: any[] = [];

    gridData.forEach(element => {
      if (!shouldShowFinding(showAll, showML, element.isReviewed || element.isQaReviewed)) {
        return;
      }

      /////this should be removed
      const [blade, bladeSide] = getBladeAndBladeSideFromSequencePath(element.sequencePath);
      /////

      let newData: any = {
        description: element.description,
        findingTypeId: element.findingTypeId,
        findingType: element.findingTypeName,
        id: element.id,
        properties: element.properties,
        key: element.id,
        shape: element.shape,
        user: element.user,
        dateCreated: element.dateCreated,
        statusText: element.statusText,
        newStatus: element.newStatus,
        dirty: element.dirty,
        responseLevel: element.responseLevel,
        userId: element.userId,
        imageName: element.imageName,
        sequencePath: element.sequencePath,
        blade,
        bladeSide,
        repairEstimateHours: element.repairEstimateHours,
        failureMechanismTemplateId: element.failureMechanismTemplateId,
        findingTypeSuggestion: element.findingTypeSuggestion,
        findingTypeMlSuggestion: element.findingTypeMlSuggestion,
        canBeDeleted: element.canBeDeleted,
        showQAReviewPassedOption: element.showQAReviewPassedOption
      };

      element.properties.forEach(prop => {
        newData[prop.name] = localizationPropertyNames.includes(prop.name) ? prepareNumberValue(prop.value, prop.name) : prop.value;
      });

      dataSource.push(newData);
    });

    columns = sortPerStandardColumns(columns);
    columns = sortPerSavedItems(columns);

    return {
      columns,
      dataSource
    };
  }
);

//special case of getColumnFilters
const getBladeAndBladeSideColumnFilters = (gridData: any[]) => {
  const blades = gridData.map(x => {
    const [blade] = getBladeAndBladeSideFromSequencePath(x.sequencePath);
    return blade;
  });
  const bladeSides = gridData.map(x => {
    const [_, bladeSide] = getBladeAndBladeSideFromSequencePath(x.sequencePath);
    return bladeSide;
  });

  const uniqueBladeFilters = [...new Set(blades)];
  const bladeFilters: ColumnFilterItem[] = uniqueBladeFilters.filter(x => !!x).map(x => ({ text: x.toString(), value: x }));

  const uniqueBladeSideFilters = [...new Set(bladeSides)];
  const bladeSideFilters: ColumnFilterItem[] = uniqueBladeSideFilters.filter(x => !!x).map(x => ({ text: x.toString(), value: x }));

  return [bladeFilters, bladeSideFilters];
};

const getStatusColumnFilters = (gridData: any[]): StatusColumnItem[] => {
  const rawFilters = gridData.map(x => x.statusText);
  const uniqueFilters = [...new Set(rawFilters)];
  const filters: StatusColumnItem[] = uniqueFilters.map(x => ({ text: x, value: x, group: getStatusGroup(x), checked: false }));
  return filters;
};

const getColumnFilters = (gridData: any[], propertyName: string): any[] => {
  const rawFilters = gridData.map(x =>
    x[propertyName]
      ? x[propertyName]
      : x.properties.find((z: any) => z.name === propertyName)
      ? x.properties.find((y: any) => y.name === propertyName).value
      : ''
  );
  const uniqueFilters = [...new Set(rawFilters)];
  const filters: ColumnFilterItem[] = uniqueFilters
    .filter(x => !!x)
    .map(x => (localizationPropertyNames.includes(propertyName) ? prepareNumberValue(x, propertyName) : x))
    .map(x => ({ text: x.toString(), value: x }));
  return filters;
};

const getColumnFiltersSlider = (gridData: any[], propertyName: string): ColumnFilterNumberItem[] => {
  const rawFilters = gridData.map(x =>
    x[propertyName]
      ? x[propertyName]
      : x.properties.find((z: any) => z.name === propertyName)
      ? x.properties.find((y: any) => y.name === propertyName).value
      : ''
  );
  const uniqueFilters = [...new Set(rawFilters)];
  const filters: ColumnFilterNumberItem[] = uniqueFilters
    .filter(x => !!x)
    .map(x => prepareNumberValue(x, propertyName))
    .map(x => ({ text: x.toString(), value: +x }))
    .sort((a, b) => {
      if (a.value < b.value) {
        return -1;
      }
      if (a.value > b.value) {
        return 1;
      }
      return 0;
    });
  return filters;
};

export const localizationPropertyNames = ['DistanceToRootM', 'DistanceToLeadingEdgeM', 'LengthMm', 'HeightMm', 'WidthMm', 'AreaMm2'];

export const partiallyReviewedStatuses = ['Waiting for review', 'QA review failed', 'Waiting for assessment', 'Under investigation'];

const getCommonColumnProperties = (propertyName: string, showSorterDirections: boolean = false, addDataIndex: boolean = true) => {
  return {
    dataIndex: addDataIndex ? propertyName : undefined,
    key: propertyName,
    width: getColumnWidth(propertyName),
    sorter: localizationPropertyNames.includes(propertyName) ? sorterFunctionForNumbers(propertyName) : sorterFunction(propertyName),
    sortDirections: showSorterDirections ? ['ascend', 'descend'] : undefined,
    showSorterTooltip: false,
    isVisible: true,
    ellipsis: true,
    onFilter: filterFunction(propertyName),
    align: localizationPropertyNames.includes(propertyName) ? 'right' : '',
    className: localizationPropertyNames.includes(propertyName) ? styles.rightPadding20 : ''
  };
};

const getCommonColumnPropertiesWithUnites = (
  propertyName: string,
  filters: ColumnFilterNumberItem[],
  showSorterDirections: boolean = false,
  addDataIndex: boolean = true
) => {
  const minValue = filters[0] ? filters[0].value : 0;
  const maxValue = filters[filters.length - 1] ? filters[filters.length - 1].value : 0;
  return {
    dataIndex: addDataIndex ? propertyName : undefined,
    key: propertyName,
    width: getColumnWidth(propertyName),
    sorter: localizationPropertyNames.includes(propertyName) ? sorterFunctionForNumbers(propertyName) : sorterFunction(propertyName),
    sortDirections: showSorterDirections ? ['ascend', 'descend'] : undefined,
    showSorterTooltip: false,
    isVisible: true,
    ellipsis: true,
    align: localizationPropertyNames.includes(propertyName) ? 'right' : '',
    className: localizationPropertyNames.includes(propertyName) ? styles.rightPadding20 : '',
    filterDropdown: (filterProps: FilterDropdownProps) => (
      <SliderFilter
        {...{
          filterProps,
          minValue,
          maxValue
        }}
      />
    ),
    onFilter: sliderFilterFunction(propertyName)
  };
};

const getStatusColumnProperties = (
  propertyName: string,
  filters: StatusColumnItem[],
  showSorterDirections: boolean = false,
  addDataIndex: boolean = true
) => {
  return {
    dataIndex: addDataIndex ? propertyName : undefined,
    key: propertyName,
    width: getColumnWidth(propertyName),
    sorter: sorterFunction(propertyName),
    sortDirections: showSorterDirections ? ['ascend', 'descend'] : undefined,
    showSorterTooltip: false,
    isVisible: true,
    ellipsis: true,
    align: 'right',
    className: styles.rightPadding20,
    filterDropdown: (filterProps: FilterDropdownProps) => (
      <StatusFilter
        {...{
          filterProps,
          filters
        }}
      />
    ),
    onFilter: filterFunction(propertyName)
  };
};

const sliderFilterFunction = (propertyName: string) => {
  return (value: string | number | boolean | number[], record: any) => {
    const recordValue = propertyName == 'AreaMm2' ? Math.trunc(+record[propertyName] / 100) : +record[propertyName];
    if (Array.isArray(value)) {
      if ((value.length = 2) && typeof value[0] == 'number' && typeof value[1] == 'number') {
        return value[0] <= recordValue && recordValue <= value[1];
      }
    }
    return false;
  };
};

const filterFunction = (propertyName: string) => {
  return (value: string | number | boolean, record: any) => (record[propertyName] ? record[propertyName] === value : false);
};

const sorterFunction = (propertyName: string) => {
  return (a: any, b: any) => {
    if (a[propertyName] && b[propertyName]) {
      return a[propertyName].localeCompare(b[propertyName]);
    }

    if (!a[propertyName]) {
      return -1;
    }

    if (!b[propertyName]) {
      return 1;
    }
  };
};

const sorterFunctionForNumbers = (propertyName: string) => {
  return (a: any, b: any) => {
    if (a[propertyName] && b[propertyName]) {
      return Number.parseFloat(a[propertyName]) - Number.parseFloat(b[propertyName]);
    }

    if (!a[propertyName]) {
      return -1;
    }

    if (!b[propertyName]) {
      return 1;
    }
  };
};

export const renderField = (key: string, propertyUnit: string) => {
  return (value: any) => {
    if ((key === 'DistanceToRootM' || key === 'DistanceToLeadingEdgeM') && !isStringNullOrEmpty(value)) {
      return (
        parseFloat(value).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 }) + ' ' + (propertyUnit ? propertyUnit : '')
      );
    }
    if ((key === 'LengthMm' || key === 'WidthMm' || key === 'HeightMm') && !isStringNullOrEmpty(value)) {
      return (
        parseFloat(value).toLocaleString('en-US', { maximumFractionDigits: 0, minimumFractionDigits: 0 }) + ' ' + (propertyUnit ? propertyUnit : '')
      );
    }
    if (key === 'AreaMm2' && !isStringNullOrEmpty(value)) {
      return (parseFloat(value) / 100).toLocaleString('en-US', { maximumFractionDigits: 0, minimumFractionDigits: 0 }) + ' cm²';
    }
    if ((key.endsWith('Mm') || key.endsWith('Mm2')) && !isStringNullOrEmpty(value)) {
      return parseInt(value).toLocaleString('sv-SE', { maximumFractionDigits: 0 }) + (propertyUnit ? propertyUnit : '');
    }

    return value;
  };
};

const getPropertyFullName = (key: string, allProperties: PropertyColumnData[]) => {
  var property = allProperties.find(x => x.name === key)!;
  return property.fullName;
};

const getPropertyUnit = (key: string, allProperties: PropertyColumnData[]) => {
  var property = allProperties.find(x => x.name === key)!;
  return property.unit;
};

const sortPerSavedItems = (columns: ColumnProps[]) => {
  const savedColumnsOrderRaw = localStorage.getItem(SAVED_COLUMNS_ORDER_KEY);

  if (savedColumnsOrderRaw) {
    const savedColumnsOrder: string[] = JSON.parse(savedColumnsOrderRaw);
    return sortColumns(columns, ['actions', ...savedColumnsOrder]);
  }

  return columns;
};

const sortPerStandardColumns = (columns: ColumnProps[]): ColumnProps[] => {
  const common = ['actions', 'findingType'];
  const srgeStandard = ['layer', 'severity'];
  const bladenaStandard = ['zone', 'actionCategory', 'damageDescription'];

  if (srgeStandard.every(x => columns.map(y => y.key.toLowerCase()).includes(x.toLowerCase()))) {
    return sortColumns(columns, [...common, ...srgeStandard]);
  }

  if (bladenaStandard.every(x => columns.map(y => y.key.toLowerCase()).includes(x.toLowerCase()))) {
    return sortColumns(columns, [...common, ...bladenaStandard]);
  }

  return columns;
};

const getStatusGroup = (status: string) => {
  return partiallyReviewedStatuses.find(x => x === status) ? 1 : 2;
};

const sortColumns = (allColumns: ColumnProps[], sortedKeyColumns: string[]): ColumnProps[] => {
  const sortedColumns: ColumnProps[] = [];

  sortedKeyColumns.forEach(columnKey => {
    const column = allColumns.find(x => x.key.toLowerCase() === columnKey.toLowerCase());
    if (!column) return;
    sortedColumns.push(column);
  });

  allColumns.forEach(column => {
    if (sortedColumns.find(x => x.key.toLowerCase() === column.key.toLowerCase())) return;
    sortedColumns.push(column);
  });

  return sortedColumns;
};

export const prepareNumberValue = (num: any, name: string) => {
  const isNumberDecimal = num - Math.floor(num) !== 0;

  if (isNumberDecimal) {
    if (name.endsWith('Mm') || name.endsWith('Mm2')) {
      return Math.trunc(num);
    }
    return Number(num).toFixed(2);
  } else {
    return num;
  }
};

const ColumnWidths = new Map<string, string>([
  ['actions', '120px'],
  ['findingType', '170px'],
  ['DistanceToRootM', '225px'],
  ['DistanceToLeadingEdgeM', '225px'],
  ['DamageDescription', '215px'],
  ['Zone', '110px'],
  ['Layer', '110px'],
  ['ActionCategory', '190px'],
  ['blade', '120px'],
  ['Severity', '130px'],
  ['AreaMm2', '150px'],
  ['WidthMm', '150px'],
  ['HeightMm', '150px'],
  ['LengthMm', '160px'],
  ['findingTypeMlSuggestion', '265px'],
  ['bladeSide', '150px'],
  ['statusText', '130px']
]);

const getColumnWidth = (column: string) => {
  if (ColumnWidths.has(column)) {
    return ColumnWidths.get(column)!;
  }

  return '200px';
};

export const reorder = (list: any[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};
